import { useEffect, useRef, useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Button } from '@/shared/components/ui/button';
import { useMfaChallengeStore } from '../../store/mfa-challenge-store';

export function MfaVerifyModal() {
  const { isOpen, submit, cancel } = useMfaChallengeStore();
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state and auto-focus input when modal opens
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      // Schedule state resets in a microtask to avoid synchronous setState in effect
      const timer = setTimeout(() => {
        setCode('');
        setError('');
        setIsSubmitting(false);
        inputRef.current?.focus();
      }, 50);
      prevOpenRef.current = true;
      return () => clearTimeout(timer);
    }
    if (!isOpen) {
      prevOpenRef.current = false;
    }
  }, [isOpen]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const trimmed = code.trim();
    if (trimmed.length < 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsSubmitting(true);
    setError('');
    submit(trimmed);
  }

  function handleCancel() {
    if (isSubmitting) return;
    cancel();
  }

  function handleOpenChange(open: boolean) {
    if (!open) {
      handleCancel();
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            MFA Verification Required
          </DialogTitle>
          <DialogDescription>
            This action requires multi-factor authentication. Enter the 6-digit code from your
            authenticator app to continue.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="mfa-code">Authentication Code</Label>
            <Input
              ref={inputRef}
              id="mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={8}
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/[^0-9a-zA-Z]/g, ''));
                setError('');
              }}
              aria-invalid={!!error}
              className={error ? 'border-destructive' : ''}
              disabled={isSubmitting}
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
            <p className="text-xs text-muted-foreground">
              You can also use a backup code if you don't have access to your authenticator.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || code.trim().length < 6}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
